/**
 * Learning — the curriculum, measured against the gap it exists to close.
 *
 * The organising idea: a course carries the readiness criterion it closes, so
 * the catalogue can be scored against the instrument instead of just listed.
 * That join is what surfaces the finding this page leads with — IGNITE's own 67
 * workshops teach Formation, Finance and Marketing, and teach toward NONE of the
 * eleven procurement gates that every assessed business fails.
 *
 * Provenance is marked on every row. 67 courses came from IGNITE's
 * Workshops_Programs__c; 14 were authored to demonstrate the gate→course link
 * and are badged "Demo". Everything else in this product is the customer's real
 * data, so the exception is labelled rather than blended in.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { PageHeader } from '../shell';
import { StatCard } from '../organisms/StatCard';
import { ChartCard, HBars } from '../organisms/Charts';

const LINK = { textDecoration: 'none', color: 'inherit', display: 'block' } as const;

interface Gate {
  id: string; gate: string; domain: string;
  businessesFailing: number; courses: number; existingCourses: number;
}
interface Course {
  id: string; code: string; title: string; summary: string | null;
  domain: string | null; level: string; durationMinutes: number | null;
  isDemo: boolean; fromSalesforce: boolean;
  closesGate: string | null; capability: string | null; modules: number;
}
interface Overview {
  totals: { courses: number; fromSalesforce: number; authored: number; closesAGate: number; modules: number; enrollments: number };
  byDomain: { domain: string; courses: number; fromSalesforce: number; authored: number }[];
  gates: Gate[];
  gatesCoveredByExistingCurriculum: number;
  criticalGateCount: number;
  catalogue: Course[];
}

export function Learning() {
  // The domain bars link back into this page with ?domain=, so it has to be read
  // or the bar is a link that appears to do nothing.
  const [searchParams] = useSearchParams();
  const [domain, setDomain] = useState(() => searchParams.get('domain') ?? '');
  const [q, setQ] = useState('');
  // 'all' | 'theirs' (migrated) | 'gates' (authored to close a criterion)
  const [source, setSource] = useState<'all' | 'theirs' | 'gates'>('all');

  const query = useQuery<Overview>({
    queryKey: ['learning-overview'],
    queryFn: ({ signal }) => api.get<Overview>('/api/learning/overview', signal),
  });

  const filtered = useMemo(() => {
    const rows = query.data?.catalogue ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((c) =>
      (!domain || (c.domain ?? 'Not mapped to a domain') === domain)
      && (source === 'all'
        || (source === 'theirs' && c.fromSalesforce)
        || (source === 'gates' && !!c.closesGate))
      && (!needle || `${c.title} ${c.summary ?? ''} ${c.capability ?? ''}`.toLowerCase().includes(needle)));
  }, [query.data, domain, q, source]);

  if (query.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  if (query.isError || !query.data) {
    return (
      <div style={{ padding: 'var(--sp-4)', borderRadius: 'var(--r-lg)', background: 'var(--color-status-red-bg)', color: 'var(--color-status-red)' }}>
        Could not load the curriculum. {(query.error as Error)?.message ?? ''}
      </div>
    );
  }

  const d = query.data;
  const uncovered = d.criticalGateCount - d.gatesCoveredByExistingCurriculum;

  return (
    <>
      <PageHeader
        title="Learning"
        subtitle={`${d.totals.courses} courses · ${d.totals.fromSalesforce} from IGNITE's own catalogue, ${d.totals.authored} authored to close a readiness gate`}
      />

      <section style={{ display: 'grid', gap: 'var(--sp-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', marginBottom: 'var(--sp-5)' }}>
        <Link to="/courses" className="ig-interactive" style={LINK}><StatCard label="Courses" value={d.totals.courses} caption={`${d.totals.modules} modules`} /></Link>
        {/* These two filter the catalogue below rather than navigating away —
            the set they count is on this page, so leaving it would be wrong. */}
        <StatCard
          label="From IGNITE" value={d.totals.fromSalesforce} caption="migrated workshops & programmes"
          style={{ cursor: 'pointer' }} onClick={() => { setSource('theirs'); setDomain(''); }}
        />
        <StatCard
          label="Closes a gate" value={d.totals.closesAGate} caption="mapped to a readiness criterion" accent="amber"
          style={{ cursor: 'pointer' }} onClick={() => { setSource('gates'); setDomain(''); }}
        />
        <Link to="/course-enrollments" className="ig-interactive" style={LINK}><StatCard label="Enrolments" value={d.totals.enrollments} caption="businesses on a course" accent="info" /></Link>
      </section>

      {/* ── The finding ─────────────────────────────────────────────────── */}
      <section
        aria-labelledby="gap-heading"
        style={{
          background: uncovered > 0 ? 'var(--color-status-red-bg)' : 'var(--color-card)',
          border: `1px solid ${uncovered > 0 ? 'var(--color-status-red)' : 'var(--color-border)'}`,
          borderRadius: 'var(--r-2xl)', padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)',
        }}
      >
        <h2 id="gap-heading" style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)' }}>
          The curriculum gap
        </h2>
        <p style={{ margin: '6px 0 var(--sp-4)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', lineHeight: 'var(--lh-normal)', maxWidth: 820 }}>
          Eleven credentials decide whether a corporate buyer will onboard a supplier at all, and{' '}
          <Link to="/reports/procurement-gates" style={{ color: 'var(--brand-primary)' }}>every assessed business fails all of them</Link>.{' '}
          {uncovered > 0 ? (
            <strong style={{ color: 'var(--color-status-red)' }}>
              IGNITE's existing {d.totals.fromSalesforce}-course catalogue teaches toward {d.gatesCoveredByExistingCurriculum} of the {d.criticalGateCount}.
            </strong>
          ) : (
            <strong>Every gate now has curriculum behind it.</strong>
          )}{' '}
          That is not a fault in the catalogue — it was built for startup and finance coaching and does that well. It is
          the specific, measurable curriculum a supplier-readiness programme has to add, and it is only visible because
          the assessment and the curriculum share one vocabulary.
        </p>

        <ul style={{ display: 'grid', gap: 'var(--sp-2)', margin: 0, padding: 0, listStyle: 'none' }}>
          {d.gates.map((g) => (
            <li key={g.id}>
            <Link
              to={g.courses > 0 ? `/courses?criterion_id=${encodeURIComponent(g.id)}` : `/assessment-criteria?domain=${encodeURIComponent(g.domain)}`}
              className="ig-interactive"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)',
                background: 'var(--color-card)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-md)', padding: '9px 12px', flexWrap: 'wrap',
                textDecoration: 'none', color: 'inherit',
              }}
            >
              <span style={{ fontSize: 'var(--fs-sm)', minWidth: 0, flex: '1 1 240px' }}>
                <strong>{g.gate}</strong>
                <span style={{ color: 'var(--color-ink-3)' }}> · {g.domain}</span>
              </span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-status-red)', whiteSpace: 'nowrap' }}>
                {g.businessesFailing} failing
              </span>
              <span style={{
                fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', whiteSpace: 'nowrap',
                padding: '2px 8px', borderRadius: 999,
                background: g.existingCourses > 0 ? 'var(--color-status-green-bg)' : 'var(--color-status-amber-bg)',
                color: g.existingCourses > 0 ? 'var(--color-status-green)' : 'var(--color-status-amber)',
              }}>
                {g.existingCourses > 0
                  ? `${g.existingCourses} existing course${g.existingCourses === 1 ? '' : 's'}`
                  : g.courses > 0 ? 'new course needed — outlined' : 'no curriculum'}
              </span>
            </Link>
            </li>
          ))}
        </ul>
      </section>

      <ChartCard title="Where the curriculum sits" caption="Courses per readiness domain, split by whether IGNITE already had them.">
        <HBars
          data={d.byDomain.map((x) => ({
            label: `${x.domain} · ${x.fromSalesforce} existing`,
            value: x.courses,
            secondary: x.authored,
            href: `/learning?domain=${encodeURIComponent(x.domain)}`,
          }))}
          valueLabel="courses in the domain"
          secondaryLabel="authored to close a gate"
          ariaLabel={`Courses per domain: ${d.byDomain.map((x) => `${x.domain}, ${x.courses}`).join('; ')}`}
        />
      </ChartCard>

      {/* ── Catalogue ───────────────────────────────────────────────────── */}
      <section style={{ marginTop: 'var(--sp-6)' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', flex: '1 1 auto' }}>
            Catalogue <span style={{ color: 'var(--color-ink-3)', fontWeight: 'var(--fw-normal)', fontSize: 'var(--fs-sm)' }}>· {filtered.length} of {d.catalogue.length}</span>
          </h2>
          <input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses…"
            aria-label="Search courses"
            style={{ padding: '7px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', minWidth: 200 }}
          />
          {(source !== 'all' || domain) && (
            <button
              onClick={() => { setSource('all'); setDomain(''); setQ(''); }}
              style={{
                padding: '7px 12px', border: '1px solid var(--brand-primary)', borderRadius: 'var(--r-md)',
                background: 'var(--brand-primary-bg)', color: 'var(--brand-primary)',
                fontSize: 'var(--fs-sm)', cursor: 'pointer',
              }}
            >
              Clear filter{source === 'theirs' ? ' · IGNITE only' : source === 'gates' ? ' · gate-closing only' : ''}
            </button>
          )}
          <select
            value={domain} onChange={(e) => setDomain(e.target.value)} aria-label="Filter by domain"
            style={{ padding: '7px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)' }}
          >
            <option value="">All domains</option>
            {d.byDomain.map((x) => <option key={x.domain} value={x.domain}>{x.domain} ({x.courses})</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gap: 'var(--sp-3)', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))' }}>
          {filtered.map((c) => (
            <Link key={c.id} to={`/courses/${c.id}`} className="ig-interactive" style={{
              background: 'var(--color-card)', border: '1px solid var(--color-border)',
              borderLeft: `3px solid ${c.closesGate ? 'var(--color-status-amber)' : 'var(--color-border-strong)'}`,
              borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 6,
              textDecoration: 'none', color: 'inherit',
            }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {c.domain && <Chip>{c.domain}</Chip>}
                <Chip tone={c.level === 'programme' ? 'info' : undefined}>{c.level}</Chip>
                {/* The one place this product shows content that is not the
                    customer's own — so it is labelled, not blended in. */}
                {c.isDemo && <Chip tone="demo">Demo content</Chip>}
              </div>
              <h3 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
                {c.title}
              </h3>
              {c.summary && (
                <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal)' }}>
                  {c.summary}
                </p>
              )}
              {c.closesGate && (
                <p style={{ margin: '2px 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
                  Closes: <strong>{c.closesGate}</strong>
                </p>
              )}
              <div style={{ marginTop: 'auto', paddingTop: 6, fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
                {c.modules > 0 ? `${c.modules} modules` : 'Catalogue entry — no modules recorded'}
                {c.durationMinutes ? ` · ${c.durationMinutes} min` : ''}
                {c.capability ? ` · ${c.capability}` : ''}
              </div>
            </Link>
          ))}
        </div>
        {filtered.length === 0 && (
          <p style={{ color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No courses match that filter.</p>
        )}
      </section>
    </>
  );
}

function Chip({ children, tone }: { children: React.ReactNode; tone?: 'info' | 'demo' }) {
  const map = {
    info: { bg: 'var(--color-status-celadon-bg)', fg: 'var(--color-status-green)' },
    demo: { bg: 'var(--color-status-gray-bg)', fg: 'var(--color-ink-3)' },
  };
  const s = tone ? map[tone] : { bg: 'var(--color-bg)', fg: 'var(--color-ink-3)' };
  return (
    <span style={{
      fontSize: 'var(--fs-xs)', padding: '2px 8px', borderRadius: 999,
      background: s.bg, color: s.fg, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}
